import React, { useState } from "react";
import { Heartbeat, Warning, Pulse, Question } from "@phosphor-icons/react";

/* ============================================================================
   HealthScorePill — Patient Health Score atom
   ----------------------------------------------------------------------------
   Drop-in pill (and optional explanation popover) for the score returned by
   /api/admin/patients(/:id). Three tiers + a fourth for insufficient data.

       healthy             ≥75   emerald
       watch               45-74 amber
       at-risk             <45   rose
       insufficient-data   null  slate

   Usage:
       <HealthScorePill score={p.health_score} tier={p.health_tier} />
       <HealthScorePill score={p.health_score} tier={p.health_tier}
                        signals={p.health_signals} size="lg" />
   ========================================================================== */

const TIERS = {
    healthy: {
        label: "Healthy",
        Icon: Heartbeat,
        cls: "bg-emerald-100 text-emerald-800 border-emerald-200",
        iconWeight: "fill",
    },
    watch: {
        label: "Watch",
        Icon: Pulse,
        cls: "bg-amber-100 text-amber-800 border-amber-200",
        iconWeight: "bold",
    },
    "at-risk": {
        label: "At risk",
        Icon: Warning,
        cls: "bg-rose-100 text-rose-800 border-rose-200",
        iconWeight: "fill",
    },
    "insufficient-data": {
        label: "Not enough data",
        Icon: Question,
        cls: "bg-slate-100 text-slate-600 border-slate-200",
        iconWeight: "bold",
    },
};

export function HealthScorePill({ score, tier, signals, size = "sm", className = "", testid }) {
    const [open, setOpen] = useState(false);
    const t = TIERS[tier] || TIERS["insufficient-data"];
    const isSm = size === "sm";

    return (
        <span className={`relative inline-flex ${className}`} data-testid={testid}>
            <button
                type="button"
                onClick={signals ? () => setOpen((v) => !v) : undefined}
                className={`inline-flex items-center gap-1 rounded-full border font-bold tracking-wide ${t.cls} ${
                    isSm ? "px-2 py-0.5 text-[10px] uppercase" : "px-2.5 py-1 text-xs"
                } ${signals ? "hover:brightness-95 cursor-help" : "cursor-default"}`}
                aria-label={`Health score: ${t.label}${score != null ? ` ${score}/100` : ""}`}
                data-testid={`${testid || "health-pill"}-trigger`}
            >
                <t.Icon size={isSm ? 10 : 12} weight={t.iconWeight} />
                {score == null ? "—" : score}
                <span className={isSm ? "hidden sm:inline" : ""}>· {t.label}</span>
            </button>

            {open && signals && (
                <span
                    onMouseLeave={() => setOpen(false)}
                    className="absolute top-full left-0 mt-1.5 z-30 w-72 rounded-xl border border-border bg-card shadow-lg p-3 text-left"
                    data-testid={`${testid || "health-pill"}-popover`}
                >
                    <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-2">
                        Score breakdown
                    </div>
                    <SignalRow
                        label="Compliance (7-day wear)"
                        weight="50%"
                        sig={signals.compliance}
                        format={(s) => s.available
                            ? `${s.wear_7d_avg ?? "—"}h/day avg`
                            : "No active aligner case"}
                    />
                    <SignalRow
                        label="Recency (last visit)"
                        weight="25%"
                        sig={signals.recency}
                        format={(s) => s.available
                            ? `${s.days_since_last_visit}d ago`
                            : "No visits yet"}
                    />
                    <SignalRow
                        label="Outstanding balance"
                        weight="25%"
                        sig={signals.balance}
                        format={(s) => s.available
                            ? (s.outstanding > 0
                                ? `₹${s.outstanding.toLocaleString("en-IN")} of ₹${s.total_billed.toLocaleString("en-IN")}`
                                : "No outstanding dues")
                            : "—"}
                    />
                    <div className="text-[10px] text-muted-foreground mt-2 pt-2 border-t border-border">
                        Higher is healthier. Score auto-recomputes each load.
                    </div>
                </span>
            )}
        </span>
    );
}

function SignalRow({ label, weight, sig, format }) {
    if (!sig) return null;
    const detail = format(sig);
    const pct = sig.max > 0 ? (sig.pts / sig.max) * 100 : 0;
    const barColor = !sig.available
        ? "bg-slate-300"
        : pct >= 75 ? "bg-emerald-500"
        : pct >= 40 ? "bg-amber-500"
        : "bg-rose-500";
    return (
        <div className="mb-2 last:mb-0">
            <div className="flex items-baseline justify-between gap-2">
                <div className="text-xs font-semibold truncate">{label}</div>
                <div className="text-[10px] text-muted-foreground tabular-nums shrink-0">
                    {sig.pts}/{sig.max} · {weight}
                </div>
            </div>
            <div className="h-1 rounded-full bg-secondary mt-1 overflow-hidden">
                <div className={`h-full ${barColor}`} style={{ width: `${Math.max(4, pct)}%` }} />
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5">{detail}</div>
        </div>
    );
}

export default HealthScorePill;
